import React, { useState } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTeam, useEditTeam, useDeleteTeam } from '../../hooks/useTeam';
import { usePermission } from '../../hooks/usePermission';
import { useDivision, useDivisionNameById } from '../../hooks/useDivision';
import DataTable from '../shared/DataTable';
import { EditTeamsPermission, DeleteTeamsPermission } from '../../constants/permissions';

const DivisionNameCell = ({ divisionId }) => {
    const { data: divisionName } = useDivisionNameById(divisionId);
    return divisionName || "";
};
  
const TeamTable = ({ handleEdit , editData }) => {
    const { data: teams, isLoading } = useTeam();
    const { data: divisions } = useDivision();
    const editTeam = useEditTeam();
    const deleteTeam = useDeleteTeam();
    const { data: canEditTeam } = usePermission({ permissionName: EditTeamsPermission });
    const { data: canDeleteTeam } = usePermission({ permissionName: DeleteTeamsPermission });

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({
        name: '',
        description: '',
        division_id: '',
        updated_at: new Date().toISOString()
    });

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedActionId, setSelectedActionId] = useState(null);

    const handleMenuClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setSelectedActionId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedActionId(null);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            await editTeam.mutateAsync({
                id: editData.id,
                data: editFormData,
            });
            setEditDialogOpen(false);
        } catch (error) {
            console.error('Error editing team:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this team?')) {
            try {
                await deleteTeam.mutateAsync(id);
                handleMenuClose();
            } catch (error) {
                console.error('Error deleting team:', error);
            }
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Description',
            selector: row => row.description || '',
            sortable: true,
        },
        {
            name: 'Division',
            selector: (row) => {
                return <DivisionNameCell divisionId={row.division_id} />;
            },
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
                    <MoreVertIcon />
                </IconButton>
            ),
            width: '100px',
        },
    ];

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <DataTable
                columns={columns}
                data={teams}
                searchFields={['name', 'description']}
                searchPlaceholder="Search teams..."
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {canEditTeam && (
                    <MenuItem onClick={() => handleEdit(teams.find(st => st.id === selectedActionId))}>
                        <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
                        <h6 className='text-blueColor'>Edit</h6>
                    </MenuItem>
                )}
                {canDeleteTeam && (
                    <MenuItem onClick={() => handleDelete(selectedActionId)}>
                        <i className="fa-solid fa-trash text-red-600 mr-2" />
                        <h6 className='text-blueColor'>Delete</h6>
                    </MenuItem>
                )}
            </Menu>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle className="bg-blueColor text-white py-4">
                    <i className="fas fa-edit text-xl mx-2"></i>
                    <span className="text-xl font-semibold">Edit Team</span>
                </DialogTitle>
                <form onSubmit={handleEditSubmit}>
                    <DialogContent className="mt-4">
                        <div className="grid grid-cols-1 gap-4 p-4">
                            <div>
                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                    Name
                                </label>
                                <input
                                    name="name"
                                    value={editFormData.name}
                                    onChange={handleInputChange}
                                    type="text"
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={editFormData.description}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                                    rows="4"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                    Division
                                </label>
                                <select
                                    name="division_id"
                                    value={editFormData.division_id}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                                >
                                    <option value="">Select Division</option>
                                    {divisions?.map((division) => (
                                        <option key={division.id} value={division.id}>
                                            {division.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex justify-end gap-4 mt-6 p-4">
                            <button
                                onClick={() => setEditDialogOpen(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blueColor text-white rounded"
                            >
                                Save
                            </button>
                        </div>
                    </DialogContent>
                </form>
            </Dialog>
        </>
    );
};

export default TeamTable; 